import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  ButtonBase,
  Fade,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import { keyframes } from '@mui/system';
import { Link } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import backgroundHero from '../images/BackgroundHeroSection.jpg';
import heroLogo from '../images/lantena_white_herosection.png';

import rentCollectionImg from '../images/Rent Collection and Accounting.png';
import tenantManagementImg from '../images/Tenant Management.png';
import propertyManagementImg from '../images/Property Management.png';
import assessmentImg from '../images/Assessment of Rent and Service Charges.png';
import mriFilingImg from '../images/MRI and Filing.png';
import propertyMarketingImg from '../images/Property Marketing.png';
import projectManagementImg from '../images/Project Management.png';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Landing = () => {
  const [selectedService, setSelectedService] = useState(null);
  const serviceContainerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Landing page services
  const services = [
    {
      title: 'Rent Collection & Accounting',
      image: rentCollectionImg,
      description:
        'Experience efficient digital rent collection, automated tracking, and transparent monthly statements for smooth financial management.',
    },
    {
      title: 'Tenant Management',
      image: tenantManagementImg,
      description:
        'Utilize smart screening, automated lease processing, and intuitive communication tools to enhance tenant relations.',
    },
    {
      title: 'Property Management',
      image: propertyManagementImg,
      description:
        'Modernize your property oversight with innovative tools for maintenance coordination and performance optimization.',
    },
    {
      title: 'Assessment of Rent & Service Charges',
      image: assessmentImg,
      description:
        'Leverage advanced market analytics and interactive reporting to set competitive rates and optimize costs.',
    },
    {
      title: 'MRI & Filing',
      image: mriFilingImg,
      description:
        'Keep your property records organized with secure digital filing and automated compliance reviews.',
    },
    {
      title: 'Property Marketing',
      image: propertyMarketingImg,
      description:
        'Boost your property\'s visibility using immersive virtual tours, targeted campaigns, and professional branding strategies.',
    },
    {
      title: 'Project Management',
      image: projectManagementImg,
      description:
        'Drive projects with real-time tracking, automated scheduling, and smart contractor coordination for timely delivery.',
    },
  ];

  const handleCardClick = (service) => {
    setSelectedService(service);
  };

  const handleCloseOverlay = (e) => {
    e.stopPropagation();
    setSelectedService(null);
  };

  const scrollLeft = () => {
    if (serviceContainerRef.current) {
      serviceContainerRef.current.scrollBy({ left: -150, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (serviceContainerRef.current) {
      serviceContainerRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Landing Page Content */}
      <Box sx={{ overflow: 'visible' }}>
        {/* Hero Section */}
        <Box
          sx={{
            position: 'relative',
            height: { xs: '450px', sm: '480px', md: '550px' },
            backgroundImage: `url(${backgroundHero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            overflow: 'hidden',
          }}
        >
          {/* Dark overlay behind hero content */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
          />

          {/* Hero content */}
          <Container
            maxWidth="lg"
            sx={{
              position: 'relative',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 5,
              pt: { xs: 4, sm: 3 },
              px: { xs: 2, sm: 3 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: {
                  xs: '100%',
                  sm: '85%',
                  md: '80%'
                },
                px: { xs: 1, sm: 2, md: 3 },
              }}
            >
              <Box
                component="img"
                src={heroLogo}
                alt="Lantena Logo"
                sx={{
                  width: {
                    xs: '140px',
                    sm: '160px',
                    md: '180px'
                  },
                  maxWidth: {
                    xs: '50%',
                    sm: '40%',
                    md: '35%'
                  },
                  height: 'auto',
                  mb: { xs: 2, sm: 3 },
                  animation: `${fadeInUp} 1s ease`,
                  objectFit: 'contain',
                }}
              />
              <Typography
                variant="h5"
                align="center"
                color="white"
                sx={{
                  fontWeight: 300,
                  mb: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.5rem' },
                  animation: `${fadeInUp} 1s ease 0.2s both`,
                  px: { xs: 1, sm: 0 },
                  maxWidth: { xs: '100%', sm: '90%' },
                  lineHeight: { xs: 1.4, sm: 1.5 },
                }}
              >
                Your Trusted, Accountable and Transparent Property and Project Partner
              </Typography>
              <Typography
                variant="body1"
                align="center"
                color="white"
                sx={{
                  mb: { xs: 2.5, sm: 3 },
                  maxWidth: '100%',
                  fontSize: { xs: '0.75rem', sm: '0.85rem', md: '1rem' },
                  animation: `${fadeInUp} 1s ease 0.4s both`,
                  px: { xs: 1, sm: 0 },
                  lineHeight: { xs: 1.3, sm: 1.5 },
                }}
              >
                Elevating Property Management with Innovation, Reliability, and Personalized Service Excellence
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1.5, sm: 2 },
                  width: { xs: '100%', sm: 'auto' },
                  animation: `${fadeInUp} 1s ease 0.6s both`,
                  mt: { xs: 1, sm: 0 },
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  size="medium"
                  sx={{
                    backgroundColor: '#f66b10',
                    color: 'white',
                    px: { xs: 3, sm: 3, md: 4 },
                    py: { xs: 1, sm: 1, md: 1.5 },
                    fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: 2,
                    minWidth: { xs: '140px', sm: 'auto' },
                    width: { xs: '70%', sm: 'auto' },
                    '&:hover': {
                      backgroundColor: '#d65600',
                      boxShadow: 3,
                    },
                  }}
                >
                  Get Started
                </Button>
                <Button
                  component={Link}
                  to="/about-us"
                  variant="outlined"
                  size="medium"
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    px: { xs: 3, sm: 3, md: 4 },
                    py: { xs: 1, sm: 1, md: 1.5 },
                    fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                    fontWeight: 500,
                    textTransform: 'none',
                    minWidth: { xs: '140px', sm: 'auto' },
                    width: { xs: '70%', sm: 'auto' },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderColor: 'white',
                    },
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Service Section */}
        <Box
          sx={{
            backgroundColor: '#f5f5f5',
            py: isMobile ? 4 : 6,
            overflow: 'hidden',
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 500,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                position: 'relative',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 80,
                  height: 4,
                  backgroundColor: '#f66b10',
                  borderRadius: 2,
                },
              }}
            >
              Our Services
            </Typography>
            <Typography
              variant="subtitle1"
              align="center"
              color="textSecondary"
              sx={{
                mt: 3,
                mb: 5,
                fontWeight: 300,
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              Comprehensive solutions for property owners and managers
            </Typography>

            {/* Service Cards Container with Navigation Arrows */}
            <Box sx={{ position: 'relative', px: { xs: 1, sm: 4 } }}>
              {/* Left Navigation Arrow */}
              <IconButton
                onClick={scrollLeft}
                size="small"
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(246, 107, 16, 0.9)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(246, 107, 16, 1)',
                  },
                  zIndex: 2,
                  display: 'flex',
                  width: { xs: 30, sm: 40 },
                  height: { xs: 30, sm: 40 },
                }}
              >
                <ArrowBackIosNewIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>

              {/* Service Cards */}
              <Box
                ref={serviceContainerRef}
                sx={{
                  display: 'flex',
                  overflowX: 'auto',
                  scrollBehavior: 'smooth',
                  gap: 2,
                  p: 1,
                  pb: 2,
                  px: { xs: 3, sm: 1 },
                  '&::-webkit-scrollbar': {
                    height: 8,
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: 10,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#a6d1ea',
                    borderRadius: 10,
                    '&:hover': {
                      backgroundColor: '#0a233b',
                    },
                  },
                }}
              >
                {services.map((service, index) => (
                  <Card
                    key={index}
                    sx={{
                      minWidth: { xs: 220, sm: 260 },
                      maxWidth: { xs: 220, sm: 260 },
                      flex: '0 0 auto',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <ButtonBase
                      component="div"
                      onClick={() => handleCardClick(service)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        width: '100%',
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="100"
                        image={service.image}
                        alt={service.title}
                        sx={{
                          objectFit: "contain",
                          padding: { xs: 1, sm: 2 },
                          backgroundColor: '#f8f8f8'
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, width: '100%', p: { xs: 1.5, sm: 2 } }}>
                        <Typography
                          gutterBottom
                          variant="h6"
                          component="h3"
                          align="left"
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            minHeight: { xs: 40, sm: 48 },
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {service.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="left"
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            lineHeight: 1.5,
                          }}
                        >
                          {service.description}
                        </Typography>
                      </CardContent>
                    </ButtonBase>
                  </Card>
                ))}
              </Box>

              {/* Right Navigation Arrow */}
              <IconButton
                onClick={scrollRight}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(246, 107, 16, 0.9)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(246, 107, 16, 1)',
                  },
                  zIndex: 2,
                  display: 'flex',
                  width: { xs: 30, sm: 40 },
                  height: { xs: 30, sm: 40 },
                }}
              >
                <ArrowForwardIosIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Box>

            {/* Service Details Overlay */}
            <Fade in={Boolean(selectedService)}>
              <Box
                sx={{
                  display: selectedService ? 'flex' : 'none',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  zIndex: 1300,
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 2,
                }}
                onClick={handleCloseOverlay}
              >
                {selectedService && (
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: 3,
                      maxWidth: { xs: '95%', sm: 700 },
                      width: '100%',
                      maxHeight: '90vh',
                      overflow: 'auto',
                      boxShadow: '0 4px 30px rgba(0,0,0,0.2)',
                      position: 'relative',
                      p: { xs: 2, sm: 4 },
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      gap: { xs: 2, md: 4 },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box
                      sx={{
                        flex: { xs: '1 1 auto', md: '0 0 40%' },
                        display: 'flex',
                        alignItems: 'flex-start',
                      }}
                    >
                      <img
                        src={selectedService.image}
                        alt={selectedService.title}
                        style={{
                          width: '100%',
                          height: 'auto',
                          borderRadius: 10,
                          boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 60%' } }}>
                      <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: '#0a233b',
                          fontSize: { xs: '1.5rem', md: '2rem' },
                        }}
                      >
                        {selectedService.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        paragraph
                        sx={{
                          mb: 2,
                          lineHeight: 1.6,
                          fontSize: { xs: '0.9rem', md: '1rem' },
                        }}
                      >
                        {selectedService.description}
                      </Typography>
                      <Typography
                        variant="body1"
                        paragraph
                        sx={{
                          mb: 3,
                          lineHeight: 1.6,
                          fontSize: { xs: '0.9rem', md: '1rem' },
                        }}
                      >
                        Our team of experts leverage cutting-edge technology
                        and proven methodologies to deliver exceptional results
                        for your property management needs.
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Button
                          component={Link}
                          to={`/${selectedService.title
                            .toLowerCase()
                            .replace(/\s/g, '-')
                            .replace(/&/g, '')
                            .replace(/--/g, '-')}`}
                          variant="contained"
                          onClick={handleCloseOverlay}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            backgroundColor: '#f66b10',
                            '&:hover': {
                              backgroundColor: '#d65600',
                            },
                            px: { xs: 2, md: 3 },
                          }}
                        >
                          Learn More
                        </Button>
                        <Button
                          component={Link}
                          to="/contact"
                          variant="outlined"
                          onClick={handleCloseOverlay}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            borderColor: '#0a233b',
                            color: '#0a233b',
                            '&:hover': {
                              backgroundColor: 'rgba(10, 35, 59, 0.05)',
                              borderColor: '#0a233b',
                            },
                            px: { xs: 2, md: 3 },
                          }}
                        >
                          Contact Us
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Fade>
          </Container>
        </Box>

        {/* Additional Landing page sections would go here */}
      </Box>
    </>
  );
};

export default Landing;
